import { useTranslation } from 'react-i18next';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { useProcessEvents } from '../hooks/usePhysicalItems';
import { formatFetchedAt, stepLabel } from '../format';
import type { CanonicalProcessName } from '../processes.api';

/**
 * Brok 5 van `mockup-processen-detail.html`: de gebeurtenissenstroom. Eén regel per gebeurtenis —
 * wanneer, welke stap, welk product — nieuwste bovenaan. Leest uit dezelfde begrensde scan als de
 * productenlijst, dus wat hier staat is het venster, niet de hele historie; dat staat er ook bij.
 */
export function GebeurtenissenStroom({ name, actief }: { name: CanonicalProcessName; actief: boolean }) {
  const { t, i18n } = useTranslation();
  const eventsQuery = useProcessEvents(name, actief);

  if (eventsQuery.isError) {
    return <ApiErrorMessage error={eventsQuery.error} onRetry={() => eventsQuery.refetch()} />;
  }

  const data = eventsQuery.data?.data;

  if (eventsQuery.isLoading || data == null) {
    return <div className="h-40 animate-pulse rounded-[var(--radius-control)] bg-[var(--background-muted)]" data-testid="stroom-loading" />;
  }

  if (data.events.length === 0) {
    return <p className="text-xs text-[var(--foreground-muted)]" data-testid="stroom-leeg">{t('processes.detail.streamEmpty')}</p>;
  }

  return (
    <div className="space-y-3" data-testid="gebeurtenissen-stroom">
      {data.truncated ? (
        <p className="text-[11px] text-[var(--info-text)]" role="status">
          {t('processes.physicalItems.truncated', { count: data.sourceEventTotal })}
        </p>
      ) : null}

      <ol className="divide-y divide-[var(--border)]">
        {data.events.map((event) => (
          <li className="flex flex-wrap items-baseline gap-x-4 gap-y-1 py-2" data-testid={`stroom-regel-${event.tradeItemId}-${event.at}`} key={`${event.tradeItemId}-${event.at}-${event.canonicalStep}`}>
            <span className="w-32 shrink-0 text-xs text-[var(--foreground-muted)]">{formatFetchedAt(event.at, i18n.language)}</span>
            <span className="text-xs font-medium text-[var(--foreground)]">{stepLabel(event.canonicalStep, t)}</span>
            <span className="text-xs text-[var(--foreground-muted)]">{event.gtin ?? event.tradeItemId}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
